import { cineamoApiClient as apiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { buildUrlQuery, UrlQueryParameter } from '../../helpers/url-query-helper';
import { CartDto } from '../../models/cart/CartDto.types';
import { CartInputDto } from '../../models/cart/CartInputDto';
import { CartItemDto } from '../../models/cart-item/CartItemDto.types';
import { InvoiceDto } from '../../models/invoice/InvoiceDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { TicketListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Cart ------------------------>

/**
 * This endpoint returns a cart
 *
 * @example
 * // Get a cart by id:
 * getCartById(1);
 *
 * @param id
 * @returns {ApiResponse<CartDto>}
 */

export async function getCartById(cartId: CartDto['id']): Promise<ApiResponse<CartDto> | null> {
    return apiClient
        .get<ApiResponse<CartDto>>(`/carts/${cartId}`)
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * This endpoint returns a cart
 *
 * @example
 * // Get a cart by uuid:
 * getCartByUuid('12345678-90ab-cdef-1234-567890abcdef');
 *
 * @param cartUuid
 * @returns {ApiResponse<CartDto>}
 */

export async function getCartByUuid(cartUuid: CartDto['uuid']): Promise<ApiResponse<CartDto> | null> {
    return apiClient
        .get<ApiResponse<CartDto>>(`/carts/${cartUuid}`, {
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Create Cart ------------------------>

/**
 * This endpoint returns a cart.
 *
 * @example
 * // Create a cart:
 * createCart({...cart});
 *
 * @param cart
 * @param options
 * @returns {ApiResponse<CartDto> | ApiErrorResponse<CartDto>}
 */

export async function createCart(
    cart: CartInputDto,
    options?: { confidentialClientAccessToken?: string }
): Promise<ApiResponse<CartDto> | null> {
    const config: RequestConfig = {};
    if (options?.confidentialClientAccessToken) {
        config.headers = {
            Authorization: 'Bearer ' + options.confidentialClientAccessToken
        };
    }

    return apiClient
        .post<ApiResponse<CartDto>>('/carts', cart, config)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Update Cart ------------------------>

/**
 * This endpoint returns a cart.
 *
 * @example
 * // Update a cart:
 * updateCartById(1, {...cart});
 *
 * @param cart
 * @param cartId
 * @returns {ApiResponse<CartDto> | ApiErrorResponse<CartDto>}
 */

export async function updateCartById(
    cartId: CartDto['id'],
    cart: CartInputDto
): Promise<ApiResponse<CartDto> | ApiErrorResponse<CartInputDto>> {
    return apiClient
        .patch(`/carts/${cartId}`, cart)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Cart ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a cart by id:
 * deleteCartById(1);
 *
 * @param cart
 * @returns {boolean}
 */

export async function deleteCartById(cartId: CartDto['id']): Promise<boolean> {
    return apiClient
        .delete(`/carts/${cartId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Download Cart assets ------------------------>

/**
 * A helper for downloading files with AJAX (axios).
 * Useful, when the endpoint requires Authorization header to be present in Request.
 *
 * @param data
 * @param filename
 */
function saveAs(data: BlobPart, filename: string) {
    const url = window.URL.createObjectURL(new Blob([data]));

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export async function downloadPdfTickets(cartUuid: CartDto['uuid']): Promise<void | null> {
    return apiClient
        .get(`/carts/${cartUuid}/tickets`, {
            headers: {
                Accept: 'application/pdf'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => saveAs(response.data as unknown as BlobPart, 'tickets.pdf'))
        .catch(() => null);
}

export async function downloadPdfTicket(cartUuid: CartDto['uuid'], ticketId: CartItemDto['id']): Promise<void | null> {
    return apiClient
        .get(`/carts/${cartUuid}/tickets/${ticketId}`, {
            headers: {
                Accept: 'application/pdf'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => saveAs(response.data as unknown as BlobPart, `ticket-${ticketId}.pdf`))
        .catch(() => null);
}

export async function downloadPdfGroupTicket(cartUuid: CartDto['uuid']): Promise<void | null> {
    return apiClient
        .get(`/carts/${cartUuid}/tickets`, {
            headers: {
                Accept: 'application/pdf'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => saveAs(response.data as unknown as BlobPart, `${cartUuid}.pdf`))
        .catch(() => null);
}

export async function getTickets(
    cartUuid: CartDto['uuid'],
    params: UrlQueryParameter
): Promise<ApiResponse<TicketListResponse>> {
    // @ts-expect-error - it must throw an error to make useQuery retry the request after a delay. This is because ticketSystemId takes some time to be assigned to the ticket after the payment.
    return apiClient
        .get(`/carts/${cartUuid}/tickets${buildUrlQuery(params)}`, {
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data);
}

type InvoiceResponse<T extends 'pdf' | 'json'> = T extends 'pdf' ? Blob : ApiResponse<InvoiceDto>;

export async function getCartInvoice<T extends 'pdf' | 'json'>(
    cartUuid: CartDto['uuid'],
    type: T = 'pdf' as T
): Promise<InvoiceResponse<T> | null> {
    return apiClient
        .get<InvoiceResponse<T>>(`/carts/${cartUuid}/invoice`, {
            headers: {
                Accept: type === 'pdf' ? 'application/pdf' : 'application/json'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

export async function downloadCartInvoice(cartUuid: CartDto['uuid']): Promise<void | null> {
    return apiClient
        .get(`/carts/${cartUuid}/invoice`, {
            headers: {
                Accept: 'application/pdf'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => saveAs(response.data as unknown as BlobPart, 'invoice.pdf'))
        .catch(() => null);
}

export async function downloadAppleWalletTicket(
    cartUuid: CartDto['uuid'],
    ticketId: CartItemDto['id']
): Promise<File | null> {
    return apiClient
        .get<File>(`/carts/${cartUuid}/tickets/${ticketId}`, {
            headers: {
                Accept: 'application/vnd.apple.pkpass'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

export async function downloadAppleWalletGroupTicket(cartUuid: CartDto['uuid']): Promise<File | null> {
    return apiClient
        .get<File>(`/carts/${cartUuid}/tickets`, {
            headers: {
                Accept: 'application/vnd.apple.pkpass'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

export async function getGoogleWalletTicket(
    cartUuid: CartDto['uuid'],
    ticketId: CartItemDto['id']
): Promise<string | null> {
    return apiClient
        .get<string>(`/carts/${cartUuid}/tickets/${ticketId}`, {
            headers: {
                Accept: 'application/vnd.google.wallet.jwt'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

export async function getGoogleWalletGroupTicket(cartUuid: CartDto['uuid']): Promise<string | null> {
    return apiClient
        .get<string>(`/carts/${cartUuid}/tickets`, {
            headers: {
                Accept: 'application/vnd.google.wallet.jwt'
            },
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}
